import { AnalyticsService, DEVICE_STORAGE_KEY } from "./analytics";
import { triggerMetaInitiateCheckoutPrediction, triggerMetaPredictionRequest } from "./meta-events";
import { UpsellPlacement } from "./entities/subscription";

/** The anonymous device id resolved by AnalyticsService.init (see DEVICE_STORAGE_KEY). */
export const getDeviceId = (): string | undefined => {
    if (typeof window === "undefined") return undefined;
    try {
        return localStorage.getItem(DEVICE_STORAGE_KEY) ?? undefined;
    } catch {
        return undefined;
    }
};

export const CourseStarted = (courseId: number, courseTitle: string, courseType: string) => {
    const event = courseType === "challenge" ? "challenge_started" : "course_started";
    const idField = courseType === "challenge" ? "challenge_id" : "course_id";
    AnalyticsService.trackEvent(event, {
        [idField]: courseId,
        course_title: courseTitle
    });
};

export const CourseCompleted = (courseId: number, courseTitle: string, courseType: string) => {
    const event = courseType === "challenge" ? "challenge_completed" : "course_completed";
    const idField = courseType === "challenge" ? "challenge_id" : "course_id";
    AnalyticsService.trackEvent(event, {
        [idField]: courseId,
        course_title: courseTitle
    });
};

export const TopicStarted = (courseId: number, topicId: number, topicTitle: string) => {
    AnalyticsService.trackEvent("topic_started", {
        course_id: courseId,
        topic_id: topicId,
        topic_title: topicTitle
    });
};

export const TopicCompleted = (courseId: number, topicId: number, topicTitle: string) => {
    AnalyticsService.trackEvent("topic_completed", {
        course_id: courseId,
        topic_id: topicId,
        topic_title: topicTitle
    });
};

type LessonEventParams = {
    courseId: number;
    topicId: number;
    lessonId: number;
    lessonTitle: string;
    courseType: string;
    dayNumber?: number;
    ver?: number;
};

export const LessonStarted = ({
    courseType,
    courseId,
    topicId,
    lessonId,
    lessonTitle,
    dayNumber
}: LessonEventParams) => {
    const event = courseType === "challenge" ? "day_started" : "lesson_started";
    const idField = courseType === "challenge" ? "challenge_id" : "course_id";
    AnalyticsService.trackEvent(event, {
        [idField]: courseId,
        topic_id: topicId,
        lesson_id: lessonId,
        lesson_title: lessonTitle,
        day_number: dayNumber
    });
};

export const LessonCompleted = ({
    courseType,
    courseId,
    topicId,
    lessonId,
    lessonTitle,
    dayNumber,
    ver
}: LessonEventParams) => {
    const event = courseType === "challenge" ? "day_completed" : "lesson_completed";
    const idField = courseType === "challenge" ? "challenge_id" : "course_id";
    const props: Record<string, unknown> = {
        [idField]: courseId,
        topic_id: topicId,
        lesson_id: lessonId,
        lesson_title: lessonTitle,
        day_number: dayNumber
    };
    if (ver != null) props.ver = ver;
    AnalyticsService.trackEvent(event, props);
};

export const dailyGoalChanged = (before: number, after: number) => {
    AnalyticsService.trackEvent("daily_goal_changed", { before, after });
};

export const dailyGoalCompleted = () => {
    AnalyticsService.trackEvent("daily_goal_completed");
};

export const lessonCompletedGoalClicked = (isChallenge = false) => {
    AnalyticsService.trackEvent(isChallenge ? "day_completed_goal_clicked" : "lesson_completed_goal_clicked");
};

export const doneForTodayClicked = () => {
    AnalyticsService.trackEvent("done_for_today_clicked");
};

export const LessonPagePresented = (lessonId: number, pageId: number, source: "next" | "skip" | "open") => {
    AnalyticsService.trackEvent("lesson_page_presented", {
        lesson_id: lessonId,
        page_id: pageId,
        source
    });
};

export const TaskStarted = (
    courseId: number,
    topicId: number,
    lessonId: number,
    taskId: number | string,
    task_type: string
) => {
    AnalyticsService.trackEvent("task_started", {
        course_id: courseId,
        topic_id: topicId,
        lesson_id: lessonId,
        task_id: taskId,
        task_type: task_type
    });
};

export const TaskSkipped = (
    courseId: number,
    topicId: number,
    lessonId: number,
    taskId: number | string,
    task_type: string
) => {
    AnalyticsService.trackEvent("task_skipped", {
        course_id: courseId,
        topic_id: topicId,
        lesson_id: lessonId,
        task_id: taskId,
        task_type: task_type
    });
};

export const TaskCompleted = (
    courseId: number,
    topicId: number,
    lessonId: number,
    taskId: number | string,
    task_type: string
) => {
    AnalyticsService.trackEvent("task_completed", {
        course_id: courseId,
        topic_id: topicId,
        lesson_id: lessonId,
        task_id: taskId,
        task_type: task_type
    });
};

export const TaskFailed = (
    courseId: number,
    topicId: number,
    lessonId: number,
    taskId: number | string,
    task_type: string
) => {
    AnalyticsService.trackEvent("task_failed", {
        course_id: courseId,
        topic_id: topicId,
        lesson_id: lessonId,
        task_id: taskId,
        task_type: task_type
    });
};

export const LoginScreenPresented = () => {
    AnalyticsService.trackEvent("login_screen_presented");
};

export const LoginScreenCompleted = () => {
    AnalyticsService.trackEvent("login_screen_completed");
};

export const LoginScreenResetPasswordClicked = () => {
    AnalyticsService.trackEvent("login_screen_reset_password_clicked");
};

interface FeedbackSubmittedProps {
    courseId: number;
    topicId?: number;
    lessonId?: number;
    type: "lesson_feedback" | "course_feedback";
    commentType?: string | null;
    comment: string;
    rating: number;
}

export const FeedbackSubmitted = ({
    courseId,
    topicId,
    lessonId,
    type,
    commentType,
    comment,
    rating
}: FeedbackSubmittedProps) => {
    AnalyticsService.trackEvent("feedback_submitted", {
        course_id: courseId,
        topic_id: topicId ? topicId : null,
        lesson_id: lessonId ? lessonId : null,
        feedback_type: type,
        comment_type: commentType,
        comment,
        rating
    });
};

interface TrustpilotEventProps {
    courseId?: number | null;
    topicId?: number | null;
    lessonId?: number | null;
    rating?: number | null;
    source?: string | null;
}

export const trackTrustpilotShown = ({ courseId, topicId, lessonId, rating, source }: TrustpilotEventProps) => {
    AnalyticsService.trackEvent("trustpilot_button_presented", {
        course_id: courseId ?? null,
        topic_id: topicId ?? null,
        lesson_id: lessonId ?? null,
        rating: rating ?? null,
        source: source ?? null
    });
};

export const trackTrustpilotSkipped = ({ courseId, topicId, lessonId, rating, source }: TrustpilotEventProps) => {
    AnalyticsService.trackEvent("trustpilot_review_closed", {
        course_id: courseId ?? null,
        topic_id: topicId ?? null,
        lesson_id: lessonId ?? null,
        rating: rating ?? null,
        source: source ?? null
    });
};

export const trackTrustpilotOpened = ({ courseId, topicId, lessonId, rating, source }: TrustpilotEventProps) => {
    AnalyticsService.trackEvent("trustpilot_button_clicked", {
        course_id: courseId ?? null,
        topic_id: topicId ?? null,
        lesson_id: lessonId ?? null,
        rating: rating ?? null,
        source: source ?? null
    });
};

export const trackAppStoreReviewRequested = (lessonId: number) => {
    AnalyticsService.trackEvent("appstore_review_requested", {
        lesson_id: lessonId
    });
};

interface ReportSubmittedProps extends Omit<FeedbackSubmittedProps, "type" | "rating" | "commentType"> {
    issueType: string;
    pageId: number;
    taskId: number | null;
}

export const ReportSubmitted = ({
    courseId,
    topicId,
    lessonId,
    pageId,
    taskId,
    issueType,
    comment
}: ReportSubmittedProps) => {
    AnalyticsService.trackEvent("report_submitted", {
        course_id: courseId,
        topic_id: topicId ? topicId : null,
        lesson_id: lessonId ? lessonId : null,
        task_id: taskId,
        page_id: pageId,
        issue_type: issueType,
        comment
    });
};

interface LessonCloseEventProps {
    courseId?: number | null;
    topicId?: number | null;
    lessonId?: number | null;
}

interface LessonCloseFeedbackEventProps extends LessonCloseEventProps {
    feedbackType: string;
    comment?: string | null;
    userId?: number | null;
}

export const trackLessonCloseClicked = ({ courseId, topicId, lessonId }: LessonCloseEventProps) => {
    AnalyticsService.trackEvent("lesson_close_clicked", {
        course_id: courseId ?? null,
        level_id: topicId ?? null,
        lesson_id: lessonId ?? null
    });
};

export const trackLessonClosePopupConfirmClicked = ({ courseId, topicId, lessonId }: LessonCloseEventProps) => {
    AnalyticsService.trackEvent("lesson_close_popup_confirm_clicked", {
        course_id: courseId ?? null,
        level_id: topicId ?? null,
        lesson_id: lessonId ?? null
    });
};

export const trackLessonClosePopupCancelClicked = ({ courseId, topicId, lessonId }: LessonCloseEventProps) => {
    AnalyticsService.trackEvent("lesson_close_popup_cancel_clicked", {
        course_id: courseId ?? null,
        level_id: topicId ?? null,
        lesson_id: lessonId ?? null
    });
};

export const trackLessonCloseFeedbackSubmitted = ({
    courseId,
    topicId,
    lessonId,
    feedbackType,
    comment,
    userId
}: LessonCloseFeedbackEventProps) => {
    AnalyticsService.trackEvent("feedback_submitted_on_close", {
        course_id: courseId ?? null,
        level_id: topicId ?? null,
        lesson_id: lessonId ?? null,
        feedback_type: feedbackType,
        comment: comment ?? "",
        user_id: userId ?? null
    });
};

export const tipViewed = ({ tipId, category }: { tipId: number; category: string }) => {
    AnalyticsService.trackEvent("tip_viewed", {
        tip_id: tipId,
        category
    });
};

export const tipPromptCopied = (tipId: number) => {
    AnalyticsService.trackEvent("tip_prompt_copied", {
        tip_id: tipId
    });
};

let hasTrackedLanding = false;

export const landingScreenPresented = (version?: string) => {
    if (hasTrackedLanding) return;
    hasTrackedLanding = true;

    AnalyticsService.trackEvent("landing_presented", {
        landing: version
    });
};

export const landingCoursesClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_courses_clicked", {
        landing: version
    });
};

export const landingReviewsClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_reviews_clicked", {
        landing: version
    });
};

export const landingRefundPolicyClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_refund_policy_clicked", {
        landing: version
    });
};

export const landingAboutUsClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_about_us_clicked", {
        landing: version
    });
};

export const landingContactClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_contact_clicked", {
        landing: version
    });
};

export const landingPricingClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_pricing_clicked", {
        landing: version
    });
};

export const landingFaqClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_FAQ_clicked", {
        landing: version
    });
};

export const landingBlogClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_blog_clicked", {
        landing: version
    });
};

export const landingHelpClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_help_clicked", {
        landing: version
    });
};

export const landingLoginClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_login_clicked", {
        landing: version
    });
};

export const landingBeginClicked = (source: string) => {
    AnalyticsService.trackEvent("landing_begin_clicked", {
        source: source
    });
};

export const landingQuestionClicked = (question: string, version?: string) => {
    AnalyticsService.trackEvent("landing_question_clicked", {
        question: question,
        landing: version
    });
};

export const landingHomeClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_home_clicked", {
        landing: version
    });
};

export const landingPrivacyClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_privacy_clicked", {
        landing: version
    });
};

export const landingAgreementClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_agreement_clicked", {
        landing: version
    });
};

export const landingSubTermsClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_sub_terms_clicked", {
        landing: version
    });
};

export const landingSupportClicked = (version?: string) => {
    AnalyticsService.trackEvent("landing_support_clicked", {
        landing: version
    });
};

export const landingSupportClickedInHeader = () => {
    AnalyticsService.trackEvent("landing_support_clicked_in_header");
};

export const contactWriteUsClicked = () => {
    AnalyticsService.trackEvent("contact_write_us_clicked");
};

export const contactHelpCenterClicked = () => {
    AnalyticsService.trackEvent("contact_help_center_clicked");
};

export const contactInquiryEmailClicked = (email: string) => {
    AnalyticsService.trackEvent("contact_inquiry_email_clicked", {
        email: email
    });
};

export const onboardingStarted = (onboarding: string, extra?: Record<string, any>) => {
    AnalyticsService.trackEvent("onboarding_started", {
        onboarding: onboarding,
        ...extra
    });
};

export const onboardingScreenPresented = ({
    onboarding,
    screenCid,
    screenIndex
}: {
    onboarding: string;
    screenCid: string;
    screenIndex: number;
}) => {
    AnalyticsService.trackEvent("screen_presented", {
        onboarding,
        screen_cid: screenCid,
        screen_index: screenIndex
    });
};

export const secondScreenPresented = ({
    onboarding,
    screenCid,
    screenIndex
}: {
    onboarding: string;
    screenCid: string;
    screenIndex: number;
}) => {
    AnalyticsService.trackEvent("second_screen_presented", {
        onboarding,
        screen_cid: screenCid,
        screen_index: screenIndex
    });
};

export const onboardingScreenCompleted = ({
    onboarding,
    screenCid,
    screenIndex,
    reply
}: {
    onboarding: string;
    screenCid: string;
    screenIndex: number;
    reply?: string | string[];
}) => {
    AnalyticsService.trackEvent("screen_completed", {
        onboarding,
        screen_cid: screenCid,
        screen_index: screenIndex,
        reply
    });
};

export const registrationScreenPresented = ({
    onboarding,
    screenCid,
    screenIndex
}: {
    onboarding: string;
    screenCid: string;
    screenIndex: number;
}) => {
    AnalyticsService.trackEvent("registration_screen_presented", {
        onboarding,
        screen_cid: screenCid,
        screen_index: screenIndex
    });
};

export const userEmailCollected = ({ onboarding, email }: { onboarding?: string; email: string }) => {
    AnalyticsService.trackEvent("user_email_collected", {
        onboarding,
        email
    });

    AnalyticsService.setUserProps({ email });
};

export const userQueryCollected = (queryParams: Record<string, string>) => {
    AnalyticsService.setUserProps(queryParams);
};

export const onboardingCompleted = ({
    onboarding,
    screenCid,
    screenIndex,
    extra
}: {
    onboarding: string;
    screenCid: string;
    screenIndex: number;
    extra?: Record<string, any>;
}) => {
    AnalyticsService.trackEvent("onboarding_completed", {
        onboarding,
        screen_cid: screenCid,
        screen_index: screenIndex,
        ...extra
    });
};

export const productScreenPresented = ({
    onboarding,
    screenCid,
    screenIndex
}: {
    onboarding: string;
    screenCid: string;
    screenIndex: number;
}) => {
    AnalyticsService.trackEvent("products_screen_presented", {
        onboarding,
        screen_cid: screenCid,
        screen_index: screenIndex
    });
};

const LOCAL_PRICE_FALLBACK_CURRENCIES = new Set(["INR", "BRL"]);

export const purchaseCompletedSuccess = ({
    onboarding,
    currency,
    price,
    provider,
    paymentId,
    subscriptionId,
    paymentMethod,
    pricecurrency,
    productId
}: {
    onboarding: string;
    currency: string;
    price: number;
    provider: string;
    paymentId: string;
    productId: string;
    pricecurrency?: number;
    subscriptionId?: string;
    paymentMethod?: string;
}) => {
    // Rupee (UPI) and real (Pix) plans may carry no USD price, which leaves the
    // USD `price` at 0: report the charge in its own currency then, as the
    // funnels do.
    const eventPrice = LOCAL_PRICE_FALLBACK_CURRENCIES.has(currency) && price === 0 && typeof pricecurrency === "number"
        ? pricecurrency
        : price;
    AnalyticsService.trackEvent("purchase_completed_success", {
        onboarding,
        currency,
        price: eventPrice,
        provider,
        payment_method: paymentMethod,
        pricecurrency: pricecurrency,
        subscription_id: subscriptionId,
        //Legacy prop names left for compatibility
        payment_id: paymentId,
        //Correct unified prop names
        transaction_id: paymentId,
        product_id: productId
    });
    triggerMetaPredictionRequest(paymentId, { currency, price: eventPrice, pricecurrency, productId });
};

export const purchaseCompletedFail = ({
    onboarding,
    currency,
    price,
    provider,
    paymentId
}: {
    onboarding: string;
    currency: string;
    price: number;
    provider: string;
    paymentId: string;
}) => {
    AnalyticsService.trackEvent("purchase_completed_fail", {
        onboarding,
        currency,
        price,
        provider,
        payment_id: paymentId
    });
};

export const metaPurchaseEventSent = ({
    value,
    currency,
    eventId
}: {
    value: number;
    currency: string;
    eventId: string;
}) => {
    AnalyticsService.trackEvent("meta_purchase_browser_event_sent", {
        value,
        currency,
        event_id: eventId,
        source: "browser"
    });
};

export const subscriptionCanceled = (subId: string, provider?: string, cancellation_reason?: string[]) => {
    AnalyticsService.trackEvent("subscription_canceled", {
        sub_id: subId,
        provider,
        cancellation_reason
    });
};

export const subscriptionRestored = (subId: string, provider?: string) => {
    AnalyticsService.trackEvent("subscription_restored", {
        sub_id: subId,
        provider
    });
};

// The restore confirmation screen (T09). restore_state is what the restore
// would do: "no_charge" (paid period still running, renews on its regular date)
// or "charge_now" (period over, Solidgate charges the renewal at once).
export type RestoreStepPayload = { subId: string; restoreState: "no_charge" | "charge_now" };

const restoreStepProperties = ({ subId, restoreState }: RestoreStepPayload) => ({
    sub_id: subId,
    restore_state: restoreState
});

export const subscriptionRestoreClicked = (payload: RestoreStepPayload) => {
    AnalyticsService.trackEvent("subscription_restore_clicked", restoreStepProperties(payload));
};

export const restoreConfirmationPresented = (payload: RestoreStepPayload) => {
    AnalyticsService.trackEvent("restore_confirmation_presented", restoreStepProperties(payload));
};

export const restoreConfirmClicked = (payload: RestoreStepPayload) => {
    AnalyticsService.trackEvent("restore_confirm_clicked", restoreStepProperties(payload));
};

export const restoreCancelClicked = (payload: RestoreStepPayload) => {
    AnalyticsService.trackEvent("restore_cancel_clicked", restoreStepProperties(payload));
};

export const checkoutPresented = ({
    onboarding,
    currency,
    price,
    provider
}: {
    onboarding: string;
    currency: string;
    price: number;
    provider: string;
}) => {
    AnalyticsService.trackEvent("checkout_presented", {
        onboarding,
        currency,
        price,
        provider
    });
};

export const checkoutScreenPresented = ({
    onboarding,
    currency,
    price,
    provider
}: {
    onboarding: string;
    currency: string;
    price: number;
    provider: string;
}) => {
    AnalyticsService.trackEvent("checkout_screen_presented", {
        onboarding,
        currency,
        price,
        provider
    });
    // The Pixel InitiateCheckout: predicted value, or none — never the real price.
    triggerMetaInitiateCheckoutPrediction();
};
export const checkoutCloseClicked = (onboarding: string) => {
    AnalyticsService.trackEvent("checkout_close_clicked", {
        onboarding: onboarding
    });
};

export const checkoutPopupShown = (onboarding: string, step: number) => {
    AnalyticsService.trackEvent(`checkout_popup_${step + 1}_shown`, {
        onboarding: onboarding
    });
};

export const checkoutPopupXClicked = (onboarding: string, step: number) => {
    AnalyticsService.trackEvent(`checkoout_popup_${step + 1}_x_clicked`, {
        onboarding: onboarding
    });
};

export const checkoutPopupContinueClicked = (onboarding: string, step: number) => {
    AnalyticsService.trackEvent(`checkoout_popup_${step + 1}_continue_clicked`, {
        onboarding: onboarding
    });
};

// Which sequence bucket the student was resolved into, and the funnel variant
// that picks the per-upsell content. Fired once, when the chain is assigned;
// every later screen event carries the same pair so funnels can be sliced by it.
export interface UpsellSequenceContext {
    onboarding: string;
    variant: string;
}

export const upsellSequenceAssigned = ({
    onboarding,
    variant,
    professionUpsells,
    paymentSystem,
    upsells
}: UpsellSequenceContext & { professionUpsells: string; paymentSystem: string; upsells: string[] }) => {
    AnalyticsService.trackEvent("upsell_sequence_assigned", {
        onboarding,
        variant,
        ...(professionUpsells ? { profession_upsells: professionUpsells } : {}),
        payment_system: paymentSystem,
        upsells: upsells.join(","),
        upsells_count: upsells.length
    });
};

export const upsellPaywallShown = (id: string, context?: UpsellSequenceContext | null) => {
    AnalyticsService.trackEvent("upsell_paywall_shown", {
        upsell: id,
        ...(context ? { onboarding: context.onboarding, variant: context.variant } : {})
    });
};

export const upsellPaywallBuyClicked = ({
    id,
    placement,
    context
}: {
    id: string;
    placement: UpsellPlacement;
    context?: UpsellSequenceContext | null;
}) => {
    AnalyticsService.trackEvent("upsell_paywall_buy_clicked", {
        upsell: id,
        placement,
        ...(context ? { onboarding: context.onboarding, variant: context.variant } : {})
    });
};

export const upsellPaywallSkipClicked = (id: string) => {
    AnalyticsService.trackEvent("upsell_paywall_skip_clicked", {
        upsell: id
    });
};

export const upsellPaywallPopupShown = (id: string, popup?: string) => {
    AnalyticsService.trackEvent("upsell_paywall_popup_shown", {
        upsell: id,
        ...(popup ? { popup } : {})
    });
};

export const upsellPaywallPopupClosed = (id: string, popup?: string) => {
    AnalyticsService.trackEvent("upsell_paywall_popup_closed", {
        upsell: id,
        ...(popup ? { popup } : {})
    });
};

export const upsellPaywallFullSkipClicked = (id: string) => {
    AnalyticsService.trackEvent("upsell_paywall_full_skip_clicked", { upsell: id });
};

interface UpsellPurchaseCompletedSuccessProps {
    currency: string;
    price: number;
    productId: string;
    pricecurrency?: number;
}

export const upsellPurchaseCompletedFail = (id: string) => {
    AnalyticsService.trackEvent("upsell_purchase_completed_fail", {
        upsell: id
    });
};

export const upsellRequestError = (error: string) => {
    AnalyticsService.trackEvent("upsell_error", {
        error
    });
};

type AnalyticsEventParams = Record<string, string | number | boolean | null | undefined>;

export const trackUniversalEvent = (eventName: string, params: AnalyticsEventParams = {}) => {
    AnalyticsService.trackEvent(eventName, params);
};

export const trackAiToolsPopularClick = (
    model: string,
    ai_assistant_open: boolean = false,
    extra: AnalyticsEventParams = {}
) => {
    AnalyticsService.trackEvent("ai_tools_popular_click", {
        model: model,
        ai_assistant_open,
        ...extra
    });
};

export const trackAiToolsGenerateClick = (type: string, ai_assistant_open: boolean = false) => {
    AnalyticsService.trackEvent("ai_tools_generate_click", {
        model: type,
        ai_assistant_open
    });
};

export const trackAiToolsQuickStartClick = (
    category: string,
    click_type: string,
    sub_category?: string,
    prompt?: string,
    ai_assistant_open: boolean = false,
    extra: AnalyticsEventParams = {}
) => {
    AnalyticsService.trackEvent("ai_tools_quickstart_click", {
        click_type: click_type,
        category: category,
        sub_category: sub_category,
        text: prompt,
        ai_assistant_open,
        ...extra
    });
};

export const trackAiToolsModelsClick = (
    type: string,
    click_type: string,
    model?: string,
    ai_assistant_open: boolean = false
) => {
    AnalyticsService.trackEvent("ai_tools_models_click", {
        click_type: click_type,
        type: type,
        model: model,
        ai_assistant_open
    });
};

export const trackAiTipsClick = (type: string, click_type: string, model?: string) => {
    AnalyticsService.trackEvent("ai_tips_click", {
        click_type: click_type,
        type: type,
        model: model
    });
};

export const trackAiToolsNewChatClick = (ai_assistant_open: boolean = false) => {
    AnalyticsService.trackEvent("ai_tools_new_chat_click", {
        ai_assistant_open
    });
};

export const trackAiToolsScreenPresented = (extra: AnalyticsEventParams = {}) => {
    AnalyticsService.trackEvent("ai_tools_screen_presented", extra);
};

export const trackLimitsAiToolsScreenClick = () => {
    AnalyticsService.trackEvent("limits_ai_tools_screen_click");
};

export const trackUpgradeChatClick = () => {
    AnalyticsService.trackEvent("upgrade_chat_click");
};

export const trackUpgradeMessageClick = () => {
    AnalyticsService.trackEvent("upgrade_message_click");
};

export const trackAiAssistantModelSelectClick = () => {
    AnalyticsService.trackEvent("ai_assistant_model_select_click");
};

export const trackAiAssistantModelSelectCloseClick = () => {
    AnalyticsService.trackEvent("ai_assistant_model_select_close_click");
};

export const trackAiAssistantModelTabClick = (tab: string) => {
    AnalyticsService.trackEvent("ai_assistant_model_tab_click", { tab });
};

export const trackAiAssistantModelClick = (model: string, tab: string) => {
    AnalyticsService.trackEvent("ai_assistant_model_click", { model, tab });
};

export const trackAiAssistantHistoryClick = () => {
    AnalyticsService.trackEvent("ai_assistant_history_click");
};

export const trackAiAssistantHistoryNewChatClick = () => {
    AnalyticsService.trackEvent("ai_assistant_history_new_chat_click");
};

export const trackAiAssistantHistoryChatClick = () => {
    AnalyticsService.trackEvent("ai_assistant_history_chat_click");
};

export const trackAiAssistantHistoryDeleteChatClick = () => {
    AnalyticsService.trackEvent("ai_assistant_history_delete_chat_click");
};

export const trackAiAssistantHistoryClose = () => {
    AnalyticsService.trackEvent("ai_assistant_history_close");
};

export const trackAiAssistantBackClick = () => {
    AnalyticsService.trackEvent("ai_assistant_back_click");
};

export const trackAiAssistantAnswer = (number: number, text: string, extra: AnalyticsEventParams = {}) => {
    AnalyticsService.trackEvent("ai_assistant_answer", { number, text, ...extra });
};

export const trackAiToolsCardSampleClicked = (extra: AnalyticsEventParams) => {
    AnalyticsService.trackEvent("ai_tools_card_sample_clicked", extra);
};

export const trackAiAssistantBackToLearningClick = () => {
    AnalyticsService.trackEvent("ai_assistant_back_to_learning_click");
};

export const trackAiAssistantStayInChatClick = () => {
    AnalyticsService.trackEvent("ai_assistant_stay_in_chat_click");
};

export const trackAiAssistantHintGotItClick = () => {
    AnalyticsService.trackEvent("ai_assistant_hint_got_it_click");
};

export const trackShownCheckoutWithDiscount = () => {
    AnalyticsService.trackEvent("checkout_popup_shown");
};

export const trackShownErrorMessagePopup = (errrorCode: string) => {
    AnalyticsService.trackEvent("unsuccessful_popup_shown", { errrorCode });
};

export const trackClickOnButtonInCheckoutWithDiscount = () => {
    AnalyticsService.trackEvent("checkout_popup_get_clicked");
};

export const trackRelatedCoursesShown = (courseId: number, sourceType: "challenge" | "course", placement: string) => {
    AnalyticsService.trackEvent("related_courses_shown", {
        source: courseId,
        source_type: sourceType,
        placement
    });
};

export const trackRelatedCoursesClick = (
    courseId: number,
    sourceType: "challenge" | "course",
    placement: string,
    target: number
) => {
    AnalyticsService.trackEvent("related_courses_click", {
        source: courseId,
        source_type: sourceType,
        placement,
        target
    });
};
export const trackClickOnButtonInErrorMessageTryAgain = () => {
    AnalyticsService.trackEvent("tryagain_button_clicked");
};

export const trackCloseErrorMessagePopup = () => {
    AnalyticsService.trackEvent("close_button_clicked");
};

export const profileScreenPresented = () => {
    AnalyticsService.trackEvent("profile_screen_presented");
};

export const profileScreenClickResubscribe = () => {
    AnalyticsService.trackEvent("profile_screen_click_resubscribe");
};

export const settingsScreenPresented = () => {
    AnalyticsService.trackEvent("settings_screen_presented");
};
export const termsScreenPresented = () => {
    AnalyticsService.trackEvent("terms_screen_presented");
};
export const subscriptionTermsScreenPresented = () => {
    AnalyticsService.trackEvent("subscription_terms_screen_presented");
};
export const policyScreenPresented = () => {
    AnalyticsService.trackEvent("policy_screen_presented");
};
export const subscriptionScreenPresented = () => {
    AnalyticsService.trackEvent("subscription_screen_presented");
};
export const billingHistoryScreenPresented = () => {
    AnalyticsService.trackEvent("billing_history_screen_presented");
};
export const billingDetailsScreenPresented = () => {
    AnalyticsService.trackEvent("billing_details_screen_presented");
};
export const trackBillingDetailsCopyClick = () => {
    AnalyticsService.trackEvent("billing_details_copy_click");
};
export const trackBillingDetailsDownloadInvoiceClick = () => {
    AnalyticsService.trackEvent("billing_details_download_invoice_click");
};
export const trackBillingHstoryViewClick = () => {
    AnalyticsService.trackEvent("billing_history_view_click");
};

export const trackNotificationsViewClick = () => {
    AnalyticsService.trackEvent("notifications_view_click");
};

export const trackSettingsBillingHstoryClick = () => {
    AnalyticsService.trackEvent("settings_billing_history_click");
};
export const canceletionFormPresented = (subscription_page_variant?: string | null) => {
    AnalyticsService.trackEvent("canceletion_form_presented", { subscription_page_variant });
};
export const canceletionFormCompleted = (cancellation_reason?: string[]) => {
    AnalyticsService.trackEvent("canceletion_form_completed", {
        cancellation_reason
    });
};

// Cancel-flow offers A/B arm ("control" | "test", flag unsubscribe_offers_variant).
// Attached only to events fired on the offer steps 4–6 so both arms can be
// compared side by side in analytics.
const withUnsubscribeOffersVariant = (props: Record<string, unknown>, unsubscribe_offers_variant?: string | null) =>
    unsubscribe_offers_variant ? { ...props, unsubscribe_offers_variant } : props;

export const unsubscribeScreenShown = (screen_name: string, unsubscribe_offers_variant?: string | null) => {
    AnalyticsService.trackEvent("unsubscribe_screen_shown", withUnsubscribeOffersVariant({ screen_name }, unsubscribe_offers_variant));
};

export const unsubscribeCancelClicked = (screen_name: string, unsubscribe_offers_variant?: string | null) => {
    AnalyticsService.trackEvent("unsubscribe_cancel_clicked", withUnsubscribeOffersVariant({ screen_name }, unsubscribe_offers_variant));
};

export const unsubscribeConfirmClicked = (screen_name: string, unsubscribe_offers_variant?: string | null) => {
    AnalyticsService.trackEvent("unsubscribe_confirm_clicked", withUnsubscribeOffersVariant({ screen_name }, unsubscribe_offers_variant));
};

export const unsubscribeReasonSubmitted = (reasons: string[]) => {
    AnalyticsService.trackEvent("unsubscribe_reason_submitted", { reasons });
};

export const unsubscribeCloseClicked = (screen_name: string, unsubscribe_offers_variant?: string | null) => {
    AnalyticsService.trackEvent("unsubscribe_close_clicked", withUnsubscribeOffersVariant({ screen_name }, unsubscribe_offers_variant));
};

const trackWithUnsubscribeOffersVariant = (eventName: string, unsubscribe_offers_variant?: string | null) => {
    if (unsubscribe_offers_variant) {
        AnalyticsService.trackEvent(eventName, { unsubscribe_offers_variant });
        return;
    }
    AnalyticsService.trackEvent(eventName);
};

export const unsubscribeFreeUpsellClicked = (unsubscribe_offers_variant?: string | null) => {
    trackWithUnsubscribeOffersVariant("unsubscribe_free_upsell_clicked", unsubscribe_offers_variant);
};

export const unsubscribeDiscountGiftClicked = (unsubscribe_offers_variant?: string | null) => {
    trackWithUnsubscribeOffersVariant("unsubscribe_discount_gift_clicked", unsubscribe_offers_variant);
};

export const unsubscribeDiscountGiftYearlyClicked = (unsubscribe_offers_variant?: string | null) => {
    trackWithUnsubscribeOffersVariant("unsubscribe_discount_gift_yearly_clicked", unsubscribe_offers_variant);
};

export const subscriptionExpiredScreenShown = (screen?: string | number) => {
    if (screen !== undefined) {
        AnalyticsService.trackEvent("subscription_expired_screen_shown", { screen });
    } else {
        AnalyticsService.trackEvent("subscription_expired_screen_shown");
    }
};

export const subscriptionExpiredScreenClickResubscribe = () => {
    AnalyticsService.trackEvent("subscription_expired_screen_click_resubscribe");
};

export const subscriptionExpiredScreenClickProfile = () => {
    AnalyticsService.trackEvent("subscription_expired_screen_click_profile");
};

export const subscriptionExpiredScreenClickSupport = () => {
    AnalyticsService.trackEvent("subscription_expired_screen_click_support");
};

export const courseVideoStarted = (
    course_id: string | number,
    topic_id: string | number,
    lesson_id: string | number,
    video_id: string | number
) => {
    AnalyticsService.trackEvent("course_video_started", {
        course_id,
        topic_id,
        lesson_id,
        video_id
    });
};

export const personalProfilePresented = () => {
    AnalyticsService.trackEvent("personal_profile_presented");
};

export const personalProfileClickEdit = () => {
    AnalyticsService.trackEvent("personal_profile_click_edit");
};

export const personalProfileClickEditConfirm = (text?: string) => {
    AnalyticsService.trackEvent("personal_profile_click_edit_confirm", { text });
};

export const personalProfileEdited = () => {
    AnalyticsService.trackEvent("personal_profile_edited");
};

export const personalProfileEditFailed = () => {
    AnalyticsService.trackEvent("personal_profile_edit_failed");
};

export const personalPlanPresented = () => {
    AnalyticsService.trackEvent("personal_plan_presented");
};

export const personalPlanClickEdit = () => {
    AnalyticsService.trackEvent("personal_plan_click_edit");
};

export const personalPlanClickEditConfirm = (text?: string) => {
    AnalyticsService.trackEvent("personal_plan_click_edit_confirm", { text });
};

export const personalPlanEdited = () => {
    AnalyticsService.trackEvent("personal_plan_edited");
};

export const personalPlanEditFailed = () => {
    AnalyticsService.trackEvent("personal_plan_edit_failed");
};

export const personalPlanClickStart = () => {
    AnalyticsService.trackEvent("personal_plan_click_start");
};

export const personalPlanClickFromHome = () => {
    AnalyticsService.trackEvent("personal_plan_click_from_home");
};

export const continueLearningModalShown = (
    courseId: number,
    sourceType: "challenge" | "course",
    levelId?: number,
    lessonId?: number,
    variant?: "control" | "test_v1"
) => {
    AnalyticsService.trackEvent("continue_learning_modal_shown", {
        source: courseId,
        source_type: sourceType,
        level_id: levelId ?? null,
        lesson_id: lessonId ?? null,
        variant: variant ?? null
    });
};

export const continueLearningClicked = (
    courseId: number,
    sourceType: "challenge" | "course",
    levelId?: number,
    lessonId?: number,
    variant?: "control" | "test_v1"
) => {
    AnalyticsService.trackEvent("continue_learning_clicked", {
        source: courseId,
        source_type: sourceType,
        level_id: levelId ?? null,
        lesson_id: lessonId ?? null,
        variant: variant ?? null
    });
};

export type RoadmapChangedPlace = "home" | "onboarding" | "re_engagement_modal";

export const roadmapChanged = (place: RoadmapChangedPlace) => {
    AnalyticsService.trackEvent("roadmap_changed", { place });
};

// Product onboarding (post-upsell "Your plan" flow) button clicks. One event
// per button so each step can be read on its own in PostHog; `extra` carries
// the funnel ids, the roadmap experiment arm and the PRD-015 arm. The
// "completed" fact is still onboardingCompleted — these fire on the click,
// before any persistence or navigation.
type ProductOnboardingClickParams = {
    onboarding: string;
    screenCid: string;
    screenIndex: number;
    extra?: Record<string, any>;
};

const trackProductOnboardingClick = (
    event: string,
    { onboarding, screenCid, screenIndex, extra }: ProductOnboardingClickParams
) => {
    AnalyticsService.trackEvent(event, {
        onboarding,
        screen_cid: screenCid,
        screen_index: screenIndex,
        ...extra
    });
};

/** Control arm, "Your plan is ready" screen: "Continue". */
export const onboardingContinueClicked = (params: ProductOnboardingClickParams) =>
    trackProductOnboardingClick("onboarding_continue_clicked", params);

/** Both arms, roadmap list: a roadmap card tapped (selection only, nothing persisted). */
export const onboardingRoadmapCardClicked = (params: ProductOnboardingClickParams) =>
    trackProductOnboardingClick("onboarding_roadmap_card_clicked", params);

/** Control arm, roadmap list: "Start learning". */
export const onboardingStartLearningClicked = (params: ProductOnboardingClickParams) =>
    trackProductOnboardingClick("onboarding_start_learning_clicked", params);

/** PRD-015 test arm, either screen: "Start first lesson" (screen_cid tells which). */
export const onboardingStartFirstLessonClicked = (params: ProductOnboardingClickParams) =>
    trackProductOnboardingClick("onboarding_start_first_lesson_clicked", params);

/** PRD-015 test arm, "Your plan" screen: "View other roadmaps" (opens the list, nothing persisted). */
export const onboardingViewOtherRoadmapsClicked = (params: ProductOnboardingClickParams) =>
    trackProductOnboardingClick("onboarding_view_other_roadmaps_clicked", params);

export const courseContinueHeroClicked = (courseId: number, sourceType: "challenge" | "course") => {
    AnalyticsService.trackEvent("course_continue_hero_clicked", {
        course_id: courseId,
        source_type: sourceType
    });
};

export const courseStartHeroClicked = (courseId: number, sourceType: "challenge" | "course") => {
    AnalyticsService.trackEvent("course_start_hero_clicked", {
        course_id: courseId,
        source_type: sourceType
    });
};

export const setPersonalPlanGroup = (value: boolean) => {
    AnalyticsService.setUserProps({ personal_plan_group: value });
};

export const setPersonalPlanOnProductOnboarding = (value: boolean) => {
    AnalyticsService.setUserProps({ personal_plan_on_product_onboarding: value });
};

export const setHasRepliesData = (value: boolean) => {
    AnalyticsService.setUserProps({ has_replies_data: value });
};

export const setPersonalPlanGroupSafe = (value: boolean) => {
    AnalyticsService.setUserProps({ personal_plan_group: value });
};

export const setPersonalPlanOnProductOnboardingSafe = (value: boolean) => {
    AnalyticsService.setUserProps({ personal_plan_on_product_onboarding: value });
};

export const setHasRepliesDataSafe = (value: boolean) => {
    AnalyticsService.setUserProps({ has_replies_data: value });
};

export const changeEmailScreenPresented = () => {
    AnalyticsService.trackEvent("change_email_screen_presented");
};
export const changeEmailScreenCompleted = () => {
    AnalyticsService.trackEvent("change_email_screen_completed");
};

export const enterCodeScreenPresented = (source: string) => {
    AnalyticsService.trackEvent("enter_code_screen_presented", { source });
};

export const enterCodeScreenCompleted = () => {
    AnalyticsService.trackEvent("enter_code_screen_completed");
};

export const setPasswordScreenPresented = () => {
    AnalyticsService.trackEvent("set_password_screen_presented");
};

export const setPasswordScreenCompleted = () => {
    AnalyticsService.trackEvent("set_password_screen_completed");
};

export const tryNowClicked = (lesson_id?: string | number, course_id?: string | number, topic_id?: string | number) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("try_now_clicked", { lesson_id, course_id, topic_id });
    }
};

export const copyClicked = (lesson_id?: string | number, course_id?: string | number, topic_id?: string | number) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("copy_clicked", { lesson_id, course_id, topic_id });
    }
};

export const retryClicked = (lesson_id?: string | number, course_id?: string | number, topic_id?: string | number) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("retry_clicked", { lesson_id, course_id, topic_id });
    }
};

export const trackAssistantGenerateEvent = (
    modelName: string,
    modelType: string,
    trackType: string,
    source?: string
) => {
    AnalyticsService.trackEvent(
        `${source ? `${source}_` : "assistant_"}${modelType}_${modelName}_generate_${trackType}`,
        { source }
    );
};

export const inlinePromptTryClicked = (
    lesson_id?: string | number,
    course_id?: string | number,
    topic_id?: string | number,
    model?: string
) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("inline_prompt_try_clicked", { lesson_id, course_id, topic_id, model });
    }
};

export const inlinePromptCopyClicked = (
    lesson_id?: string | number,
    course_id?: string | number,
    topic_id?: string | number
) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("inline_prompt_copy_clicked", { lesson_id, course_id, topic_id });
    }
};

export const inlinePromptAnswerCopyClicked = (
    lesson_id?: string | number,
    course_id?: string | number,
    topic_id?: string | number
) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("inline_prompt_answer_copy_clicked", { lesson_id, course_id, topic_id });
    }
};

export const inlinePromptRetryClicked = (
    lesson_id?: string | number,
    course_id?: string | number,
    topic_id?: string | number,
    model?: string
) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("inline_prompt_retry_clicked", { lesson_id, course_id, topic_id, model });
    }
};

export const inlinePromptImageDownloadClicked = (
    lesson_id?: string | number,
    course_id?: string | number,
    topic_id?: string | number,
    model?: string,
    url?: string
) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("inline_prompt_image_download_clicked", {
            lesson_id,
            course_id,
            topic_id,
            model,
            url
        });
    }
};

export const trackInlinePromptSuccessResponse = (
    lesson_id?: string | number,
    course_id?: string | number,
    topic_id?: string | number,
    model?: string,
    prompt?: string,
    answer?: string
) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("inline_prompt_success_response", {
            lesson_id,
            course_id,
            topic_id,
            model,
            prompt,
            answer
        });
    }
};

export const trackInlinePromptError = (
    lesson_id?: string | number,
    course_id?: string | number,
    topic_id?: string | number,
    model?: string,
    prompt?: string
) => {
    if (lesson_id && course_id && topic_id) {
        AnalyticsService.trackEvent("inline_prompt_error", {
            lesson_id,
            course_id,
            topic_id,
            model,
            prompt
        });
    }
};

export const confirmEmailChanged = ({
    purchaseEmail,
    confirmEmail
}: {
    purchaseEmail: string;
    confirmEmail: string;
}) => {
    AnalyticsService.trackEvent("confirm_email_changed", {
        purchase_email: purchaseEmail,
        confirm_email: confirmEmail
    });
};

export const confirmEmailScreenPresented = () => {
    AnalyticsService.trackEvent("confirm_email_screen_presented");
};

export const confirmEmailScreenCompleted = () => {
    AnalyticsService.trackEvent("confirm_email_screen_completed");
};

// Certificate analytics
export const certificateAwarded = (course_id?: number, course_title?: string) => {
    AnalyticsService.trackEvent("certificate_awarded", { course_id, course_title });
};

export const certificatePageOpened = (course_id?: number, certificate_id?: string) => {
    AnalyticsService.trackEvent("certificate_page_opened", { course_id, certificate_id });
};

export const certificateSharedLinkedin = (certificate_id: string, course_id?: number) => {
    AnalyticsService.trackEvent("certificate_shared_linkedin", { certificate_id, course_id });
};

export const certificateDownloaded = (certificate_id: string, source: string, course_id?: number, type?: string) => {
    AnalyticsService.trackEvent("certificate_downloaded", { certificate_id, source, course_id, type });
};

export const certificateLinkCopied = (certificate_id: string, source: string, course_id?: number) => {
    AnalyticsService.trackEvent("certificate_link_copied", { certificate_id, source, course_id });
};

export const certificateStudentNameSheetOpened = () => {
    AnalyticsService.trackEvent("certificate_student_name_sheet_opened");
};

export const certificateStudentNameSheetSubmitted = () => {
    AnalyticsService.trackEvent("certificate_student_name_sheet_submitted");
};

export const certificateShareButtonClicked = (certificate_id: string) => {
    AnalyticsService.trackEvent("certificate_share_button_clicked", { certificate_id });
};

export const certificateImageShared = (certificate_id: string) => {
    AnalyticsService.trackEvent("certificate_share_image_button_clicked", { certificate_id });
};

export const refundButtonClicked = (source: string) => {
    AnalyticsService.trackEvent("refund_button_clicked", { source });
};

export const researchReportEmailRequested = (params: { email: string; timestamp?: string }) => {
    AnalyticsService.trackEvent("research_report_email_requested", params);
};

export const homeTrackClicked = (trackName: string) => {
    AnalyticsService.trackEvent("home_track_clicked", { track_name: trackName });
};

export const homeTrackCourseClicked = ({
    orderNumber,
    trackName,
    numberInTrack
}: {
    orderNumber: number;
    trackName: string;
    numberInTrack: number;
}) => {
    AnalyticsService.trackEvent("home_track_course_clicked", {
        order_number: orderNumber,
        track_name: trackName,
        course_number: numberInTrack
    });
};

export const promptLibraryOpened = () => {
    AnalyticsService.trackEvent("prompt_library_opened");
};

export const promptLibraryUnlockClicked = (data: {
    placement: "button" | "prompt" | "banner";
    promptId?: number;
    promptTitle?: string;
    promptText?: string;
    category?: string;
    categoryId?: number;
}) => {
    const { placement, promptId, promptTitle, promptText, category, categoryId } = data;
    const eventData: Record<string, any> = { placement };

    if (placement === "prompt" && promptId && promptTitle && promptText) {
        eventData.promptId = promptId;
        eventData.promptTitle = promptTitle;
        eventData.promptText = promptText;
    } else if ((placement === "button" || placement === "banner") && category && categoryId) {
        eventData.category = category;
        eventData.categoryId = categoryId;
    }

    AnalyticsService.trackEvent("prompt_library_unlock_clicked", eventData);
};

export const promptLibraryBannerClicked = ({ placement }: { placement: "home" | "profile" | "rail" }) => {
    AnalyticsService.trackEvent("prompt_library_banner_clicked", { placement });
};

export const aiToolsBannerClicked = ({ placement, purchased }: { placement: "profile" | "rail"; purchased: boolean }) => {
    AnalyticsService.trackEvent("ai_tools_banner_clicked", { placement, purchased });
};

export const promptLibraryCategoryClicked = ({
    category,
    categoryId,
    type
}: {
    category: string;
    categoryId: number;
    type: "top" | "sub";
}) => {
    AnalyticsService.trackEvent("prompt_library_category_clicked", { category, category_id: categoryId, type });
};

export const promptLibraryTryClicked = ({
    promptText,
    promptTitle,
    id
}: {
    promptText: string;
    promptTitle: string;
    id: number;
}) => {
    AnalyticsService.trackEvent("prompt_library_try_clicked", {
        prompt_text: promptText,
        prompt_title: promptTitle,
        prompt_id: id
    });
};

export const promptLibraryCopyClicked = ({
    promptText,
    promptTitle,
    id
}: {
    promptText: string;
    promptTitle: string;
    id: number;
}) => {
    AnalyticsService.trackEvent("prompt_library_copy_clicked ", {
        prompt_text: promptText,
        prompt_title: promptTitle,
        prompt_id: id
    });
};

export const setFirstPurchaseTime = () => {
    const firstPurchaseTime = new Date().getTime();
    AnalyticsService.setUserProps({ first_purchase_time: firstPurchaseTime });
};

export const pushNotificationIntroScreenPresented = () => {
    AnalyticsService.trackEvent("push_notification_intro_screen_presented");
};

export const pushNotificationTimeScreenPresented = () => {
    AnalyticsService.trackEvent("push_notification_time_screen_presented");
};

export const pushNotificationTimeSelected = (time: string) => {
    AnalyticsService.trackEvent("push_notification_time_selected", { time });
};

export const pushNotificationConsentClicked = () => {
    AnalyticsService.trackEvent("push_notification_consent_clicked");
};

export const pushNotificationPermissionGranted = () => {
    AnalyticsService.trackEvent("push_notification_permission_granted");
};

export const pushNotificationPermissionDenied = (reason: "denied" | "error") => {
    AnalyticsService.trackEvent("push_notification_permission_denied", { reason });
};

export const pushNotificationDisabled = () => {
    AnalyticsService.trackEvent("push_notification_disabled");
};

export const emailNotificationEnabled = () => {
    AnalyticsService.trackEvent("email_notification_enabled");
};

export const emailNotificationDisabled = () => {
    AnalyticsService.trackEvent("email_notification_disabled");
};

export const subscriptionPauseButtonClicked = (pause_period: string) => {
    AnalyticsService.trackEvent("subscription_pause_button_clicked", { pause_period });
};

export const subscriptionResumeButtonClicked = (paused_until?: string | null) => {
    AnalyticsService.trackEvent("subscription_resume_button_clicked", { paused_until });
};

export const claimCertificateClicked = (course_id?: number, course_title?: string, source?: string) => {
    AnalyticsService.trackEvent("claim_certificate_clicked", { course_id, course_title, source });
};

export const changeBrowserPagePresented = () => {
    AnalyticsService.trackEvent("change_browser_page_presented");
};

export const changeBrowserPageSkipClicked = () => {
    AnalyticsService.trackEvent("change_browser_page_skip_clicked");
};

export const changeBrowserPageCloseClicked = () => {
    AnalyticsService.trackEvent("change_browser_page_close_clicked");
};

export const changeBrowserPageCopyClicked = () => {
    AnalyticsService.trackEvent("change_browser_page_copy_clicked");
};

export const roadmapStartLearningClicked = (courseId: number, sourceType: "challenge" | "course") => {
    AnalyticsService.trackEvent("roadmap_start_learning_clicked ", {
        course_id: courseId,
        source_type: sourceType
    });
};

export const roadmapCourseClicked = (courseId: number, sourceType: "challenge" | "course") => {
    AnalyticsService.trackEvent("roadmap_course_clicked ", {
        course_id: courseId,
        source_type: sourceType
    });
};

export const roadmapHeroClicked = (courseId: number, sourceType: "challenge" | "course") => {
    AnalyticsService.trackEvent("roadmap_hero_clicked  ", {
        course_id: courseId,
        source_type: sourceType
    });
};

export const roadmapCertificateClicked = (roadmapId: number) => {
    AnalyticsService.trackEvent("roadmap_certificate_clicked", {
        roadmap_id: roadmapId
    });
};

export const trackChangeDailyGoalClick = () => {
    AnalyticsService.trackEvent("change_daily_goal_clicked");
};

export const trackChangePaymentMethodClicked = () => {
    AnalyticsService.trackEvent("change_payment_method_clicked");
};

export const trackChangePaymentMethodFailed = (errorCode: string, errorMessage: string) => {
    AnalyticsService.trackEvent("change_payment_method_failed", {
        new_payment_method: "card",
        error_code: errorCode,
        error_message: errorMessage
    });
};

export const trackAppTourStarted = (stepsCount: number) => {
    AnalyticsService.trackEvent("app_tour_started", {
        steps: stepsCount
    });
};

export const trackAppTourSkipped = (stepId: string) => {
    AnalyticsService.trackEvent("app_tour_skipped", {
        step: stepId
    });
};

export const trackAppTourCompleted = () => {
    AnalyticsService.trackEvent("app_tour_completed");
};

export const trackAppTourStepViewed = (stepId: string, screen: string) => {
    AnalyticsService.trackEvent("app_tour_step_viewed", {
        step: stepId,
        screen
    });
};

export const trackAppTourStepTargetMissing = (stepId: string, screen: string) => {
    AnalyticsService.trackEvent("app_tour_step_target_missing", {
        step: stepId,
        screen
    });
};

export const trackAppTourNavPerformed = (to: string, via: "next_button" | "tab_tap") => {
    AnalyticsService.trackEvent("app_tour_nav_performed", {
        to,
        via
    });
};

export const trackAppTourBack = (fromStepId: string, toStepId: string) => {
    AnalyticsService.trackEvent("app_tour_back", {
        from: fromStepId,
        to: toStepId
    });
};

export const trackAppTourResynced = (fromStepId: string, toStepId: string, route: string) => {
    AnalyticsService.trackEvent("app_tour_resynced", {
        from: fromStepId,
        to: toStepId,
        route
    });
};

export const trackAppTourLeftPath = (stepId: string, route: string) => {
    AnalyticsService.trackEvent("app_tour_left_path", {
        step: stepId,
        route
    });
};

export const trackAppTourClosePrompted = (stepId: string) => {
    AnalyticsService.trackEvent("app_tour_close_prompted", {
        step: stepId
    });
};

export const trackAppTourCloseCancelled = (stepId: string) => {
    AnalyticsService.trackEvent("app_tour_close_cancelled", {
        step: stepId
    });
};

export const trackAppTourRestored = (stepId: string) => {
    AnalyticsService.trackEvent("app_tour_restored", {
        step: stepId
    });
};

export const roadmapCertificateLockedClicked = (roadmapId: number, progress: number) => {
    AnalyticsService.trackEvent("roadmap_certificate_locked_clicked", {
        roadmap_id: roadmapId,
        progress
    });
};

export const roadmapCertificateUnlockCtaClicked = (roadmapId: number) => {
    AnalyticsService.trackEvent("roadmap_certificate_unlock_clicked", {
        roadmap_id: roadmapId
    });
};

export const soundEffectsToggled = (enabled: boolean) => {
    AnalyticsService.trackEvent("sound_effects_toggled", { enabled });
};

export const xpAwarded = (
    source: string,
    amount: number,
    total: number,
    level: number,
    breakdown: { source: string; amount: number }[]
) => {
    AnalyticsService.trackEvent("xp_awarded", { source, amount, total, level, breakdown });
};

export const levelUp = (from: number, to: number, total: number) => {
    AnalyticsService.trackEvent("level_up", { from, to, xp_total: total });
};

export const themeChanged = (props: {
    from: string;
    to: string;
    place: string;
    resolved_theme: string;
    system_prefers_dark: boolean;
}) => {
    AnalyticsService.trackEvent("theme_changed", props);
};

export const themeSwitcherPresented = (place: string) => {
    AnalyticsService.trackEvent("theme_switcher_presented", { place });
};
